import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Lock, Loader2, LogOut, Search, Download, FileDown,
  ArrowUpDown, Users, CheckCircle2, BarChart3, ArrowRight,
} from "lucide-react";
import { api, ADMIN_TOKEN_KEY } from "../lib/api";

const errMsg = (e, fallback) =>
  e?.response?.data?.detail && typeof e.response.data.detail === "string"
    ? e.response.data.detail
    : fallback;

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return isNaN(d) ? "-" : d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
};

const LoginView = ({ onAuthed, tokenKey, title, subtitle, kicker }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!password.trim()) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password: password.trim() });
      sessionStorage.setItem(tokenKey, data.token);
      onAuthed(data.token);
    } catch (err) {
      const m = errMsg(err, "Login failed. Please try again.");
      setError(m);
      toast.error(m);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="evc-admin-login-wrap">
      <form className="evc-admin-login" onSubmit={submit} data-testid="admin-login-form">
        <span className="evc-card-kicker"><Lock size={13} strokeWidth={2.2} /> {kicker}</span>
        <h1 className="evc-admin-login-title">{title}</h1>
        <p className="evc-admin-login-sub">{subtitle}</p>
        <input
          type="password"
          className="evc-input"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          data-testid="admin-password-input"
        />
        {error && <div className="evc-error" data-testid="admin-login-error">{error}</div>}
        <button type="submit" className="evc-btn" disabled={loading} data-testid="admin-login-btn">
          {loading ? <Loader2 size={17} className="evc-spin" /> : null}
          {loading ? "Signing in..." : "Sign in"}
          {!loading && <ArrowRight size={17} />}
        </button>
      </form>
    </div>
  );
};

const Stat = ({ icon, label, value, testid }) => (
  <div className="evc-stat" data-testid={testid}>
    <div className="evc-stat-icon">{icon}</div>
    <div>
      <div className="evc-stat-value">{value}</div>
      <div className="evc-stat-label">{label}</div>
    </div>
  </div>
);

const Dashboard = ({ token, onLogout, title, kicker }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [downloading, setDownloading] = useState("");

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    let active = true;
    api
      .get("/admin/leads", authHeader)
      .then(({ data }) => { if (active) { setLeads(data.leads || []); setLoading(false); } })
      .catch((err) => {
        if (err?.response?.status === 401) { onLogout(); return; }
        toast.error(errMsg(err, "Could not load leads."));
        setLoading(false);
      });
    return () => { active = false; };
  }, [authHeader, onLogout]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = leads;
    if (term) {
      rows = rows.filter((l) =>
        [l.name, l.company, l.email].some((v) => (v || "").toLowerCase().includes(term))
      );
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sort.key === "name") return (a.name || "").localeCompare(b.name || "") * dir;
      if (sort.key === "company") return (a.company || "").localeCompare(b.company || "") * dir;
      const da = new Date(a.verified_at || a.created_at || 0).getTime();
      const dbb = new Date(b.verified_at || b.created_at || 0).getTime();
      return (da - dbb) * dir;
    });
  }, [leads, q, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const blobDownload = async (url, fallbackName, key) => {
    setDownloading(key);
    try {
      const res = await api.get(url, { ...authHeader, responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const name = match ? match[1] : fallbackName;
      const href = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = href; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(href);
    } catch (err) {
      toast.error(errMsg(err, "Download failed."));
    } finally {
      setDownloading("");
    }
  };

  const withResults = leads.filter((l) => l.has_results).length;

  return (
    <div className="evc-admin" data-testid="admin-dashboard">
      <header className="evc-admin-top">
        <div>
          <span className="evc-card-kicker"><BarChart3 size={13} strokeWidth={2.2} /> {kicker}</span>
          <h1 className="evc-admin-title">{title}</h1>
        </div>
        <button className="evc-admin-ghost" onClick={onLogout} data-testid="admin-logout-btn">
          <LogOut size={15} /> Sign out
        </button>
      </header>

      <div className="evc-admin-stats">
        <Stat icon={<Users size={18} />} label="Total leads" value={leads.length} testid="stat-total" />
        <Stat icon={<CheckCircle2 size={18} />} label="Completed assessment" value={withResults} testid="stat-results" />
      </div>

      <div className="evc-admin-toolbar">
        <div className="evc-admin-search">
          <Search size={16} className="evc-admin-search-icon" />
          <input
            className="evc-input"
            placeholder="Search name, company or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-search-input"
          />
        </div>
        <button
          className="evc-btn evc-btn-outline"
          onClick={() => blobDownload("/admin/leads.csv", "evc-leads.csv", "all")}
          disabled={downloading === "all" || leads.length === 0}
          data-testid="admin-export-all-btn"
          title="Summary only - one row per participant (no question detail)"
        >
          {downloading === "all" ? <Loader2 size={15} className="evc-spin" /> : <FileDown size={15} />}
          Overview (CSV)
        </button>
      </div>

      {loading ? (
        <div className="evc-admin-loading" data-testid="admin-loading">
          <Loader2 size={26} className="evc-spin" /> <span>Loading leads...</span>
        </div>
      ) : (
        <div className="evc-admin-tablewrap">
          <table className="evc-admin-table" data-testid="admin-leads-table">
            <thead>
              <tr>
                <th className="evc-sortable" onClick={() => toggleSort("name")}>Name <ArrowUpDown size={12} /></th>
                <th className="evc-sortable" onClick={() => toggleSort("company")}>Company <ArrowUpDown size={12} /></th>
                <th>Email</th>
                <th className="evc-sortable" onClick={() => toggleSort("date")}>Date <ArrowUpDown size={12} /></th>
                <th>Results</th>
                <th>Full report</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="evc-admin-empty" data-testid="admin-empty">No leads found.</td></tr>
              ) : (
                filtered.map((l) => {
                  const rs = l.results_summary;
                  return (
                    <tr key={l.email} data-testid={`admin-row-${l.email}`}>
                      <td className="evc-admin-name">{l.name || "-"}</td>
                      <td>{l.company || "-"}</td>
                      <td className="evc-admin-email">{l.email}</td>
                      <td className="evc-admin-date">{fmtDate(l.verified_at || l.created_at)}</td>
                      <td>
                        {l.has_results && rs ? (
                          <span className="evc-pill evc-pill-result" title={rs.archetype || ""} data-testid={`admin-result-${l.email}`}>
                            <b>{rs.source === "light" ? "Light" : "Full"}</b> · {rs.levelCode || "-"} - {typeof rs.overall === "number" ? rs.overall.toFixed(2) : "-"}
                          </span>
                        ) : (
                          <span className="evc-pill evc-pill-muted">Not completed</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="evc-admin-dl"
                          disabled={!l.has_results || downloading === l.email}
                          onClick={() => blobDownload(`/admin/results/${encodeURIComponent(l.email)}/download`, `${l.email}-results.csv`, l.email)}
                          data-testid={`admin-download-${l.email}`}
                          title={l.has_results ? "Complete report - every question, the chosen answer & all free-text notes" : "No results yet"}
                        >
                          {downloading === l.email ? <Loader2 size={14} className="evc-spin" /> : <Download size={14} />}
                          Full CSV
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function AdminLeads({
  tokenKey = ADMIN_TOKEN_KEY,
  title = "Leads dashboard",
  subtitle = "Enter the password to view who unlocked the assessment.",
  kicker = "Internal",
}) {
  const [token, setToken] = useState(() => sessionStorage.getItem(tokenKey) || "");

  const logout = () => {
    sessionStorage.removeItem(tokenKey);
    setToken("");
  };

  if (!token)
    return (
      <LoginView onAuthed={setToken} tokenKey={tokenKey} title={title} subtitle={subtitle} kicker={kicker} />
    );
  return <Dashboard token={token} onLogout={logout} title={title} kicker={kicker} />;
}
